// node modules
import React, { useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { SearchkitComponent } from 'searchkit';
import classNames from 'classnames';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

// app modules: components
import Button from 'components/Button';
import Icon from 'components/Icon';
import SearchkitDropdown from 'components/SearchkitDropdown';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// constants
import { FILTERS } from 'constants/search';
import { BREAKPOINTS } from 'constants/index';
import NUTRITION_COMPUTED_DIET_TAGS from 'constants/nutrition-diet-tags';
import { DIET_FACET_ORDER, DIET_FACET_HIDDEN } from 'constants/diet-facet-order';

// assets
import CssSearchFilters from './SearchFilters.scss';
import { useEffect } from 'react';

const ORDER = [
  '2010-present day',
  '2000s',
  '1980s & 1990s',
  '1960s & 1970s',
  'Early 20th Century',
  'Pre-20th Century',
  'Easy',
  'Medium',
  'Complex',
  'Ready in 30 minutes',
  '5 ingredients max',
];

export const sortEraFields = buckets => {
  const results = new Array(ORDER.length);

  buckets.forEach(bucket => {
    const index = ORDER.indexOf(bucket.key);

    if (index !== -1) {
      results[index] = bucket;
    }
  });

  return results.filter(bucket => typeof bucket !== 'undefined');
};

const NUTRITION_COMPUTED_DIET_TAG_SET = new Set(NUTRITION_COMPUTED_DIET_TAGS);

export const filterNutritionDietBuckets = buckets =>
  buckets.filter(
    bucket => bucket.selected || bucket.missing || !NUTRITION_COMPUTED_DIET_TAG_SET.has(bucket.key),
  );

const DIET_FACET_HIDDEN_SET = new Set(DIET_FACET_HIDDEN);

export const sortDietBuckets = buckets => {
  const results = new Array(DIET_FACET_ORDER.length);
  const leading = [];
  const trailing = [];

  buckets.forEach(bucket => {
    if (DIET_FACET_HIDDEN_SET.has(bucket.key) && !bucket.selected && !bucket.missing) {
      return;
    }

    const index = DIET_FACET_ORDER.indexOf(bucket.key);

    if (index !== -1) {
      results[index] = bucket;
    } else if (bucket.missing) {
      leading.push(bucket);
    } else {
      trailing.push(bucket);
    }
  });

  return [...leading, ...results.filter(bucket => typeof bucket !== 'undefined'), ...trailing];
};

export const filterAndSortDietBuckets = buckets =>
  sortDietBuckets(filterNutritionDietBuckets(buckets));

export function SearchFilters(props) {
  const { isVisible, useFullBookTitle, style, className, onToggle } = props;
  useStyles(CssSearchFilters);
  const dimensions = useSelector(state => state.dimensions);
  const [nutritionInfoEnabled] = useFeatureToggle('nutritionInfo');
  const [filtersVisible, setFiltersVisible] = useState(null);

  const isWideDisplay = dimensions.body > BREAKPOINTS.PHONE_VERTICAL;
  const displayInactiveFilters = isWideDisplay || filtersVisible;

  const searchFilterButtonClassName = classNames('search-filters__button', {
    'margin-bottom-5': filtersVisible,
  });

  const toggleFilters = () => {
    const newValue = !filtersVisible;
    setFiltersVisible(newValue);

    if (onToggle) {
      onToggle(newValue);
    }
  };

  useEffect(
    () => {
      if (!isVisible) {
        setFiltersVisible(false);
      }
    },
    [isVisible],
  );

  return (
    <div
      data-cy="search-filters"
      className={`search-filters ${className}`}
      hidden={!isVisible}
      style={style}
    >
      <div
        onClick={toggleFilters}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleFilters();
          }
        }}
        aria-label={filtersVisible ? 'hide filters' : 'show filters'}
        className={searchFilterButtonClassName}
      >
        <Button themeName="dusty" themeType="clear" size="large">
          <Icon
            className="search-filters__filter"
            name="filter"
            fill="currentColor"
            height="20"
            width="20"
          />
        </Button>

        <ChevronRightIcon
          fontSize="medium"
          style={{
            transform: filtersVisible ? 'rotate(-90deg)' : 'rotate(90deg)',
          }}
          className="search-filters__button__chevron-icon"
        />
      </div>

      <div className="search-filters__body">
        <SearchkitDropdown
          id="country"
          field="country.raw"
          stateKey={FILTERS.COUNTRY}
          title="Cuisine"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="course"
          field="course.raw"
          stateKey={FILTERS.COURSE}
          title="Course"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="diet_type"
          field="diet_type.raw"
          stateKey={FILTERS.DIET_TYPE}
          title="Diet"
          operator="AND"
          visibleWhenUnselected={displayInactiveFilters}
          bucketsTransform={nutritionInfoEnabled ? sortDietBuckets : filterAndSortDietBuckets}
        />
        <SearchkitDropdown
          id="skill"
          field="skill.raw"
          stateKey={FILTERS.SKILL}
          title="Complexity"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          bucketsTransform={sortEraFields}
        />
        <SearchkitDropdown
          id="era"
          field="era.raw"
          stateKey={FILTERS.ERA}
          title="Era"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          bucketsTransform={sortEraFields}
        />
        <SearchkitDropdown
          id="authors"
          field="authors.raw"
          stateKey={FILTERS.AUTHORS}
          title="Authors"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
        />
        <SearchkitDropdown
          id="book_title"
          field="book_title.raw"
          stateKey={FILTERS.BOOK_TITLE}
          title="Book"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          hidden={useFullBookTitle}
        />
        <SearchkitDropdown
          id="book_full_title"
          field="book_full_title.raw"
          stateKey={FILTERS.BOOK_FULL_TITLE}
          title="Book"
          operator="OR"
          visibleWhenUnselected={displayInactiveFilters}
          hidden={!useFullBookTitle}
        />
      </div>
    </div>
  );
}

SearchFilters.propTypes = {
  isVisible: PropTypes.bool,
  useFullBookTitle: PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  className: PropTypes.string,
  onToggle: PropTypes.func,
};

SearchFilters.defaultProps = {
  isVisible: true,
  useFullBookTitle: false,
  style: {},
  className: '',
  onToggle: null,
};

export class SearchFiltersWrapper extends SearchkitComponent {
  static propTypes = {
    isVisible: PropTypes.bool,
    style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    className: PropTypes.string,
    onToggle: PropTypes.func,
  };

  static defaultProps = {
    isVisible: true,
    style: {},
    className: '',
    onToggle: null,
  };

  render() {
    const { isVisible, style, className, onToggle } = this.props;

    // Normally the book filter uses short title. Sometimes we need to use full title.
    const useFullBookTitle =
      this.searchkit &&
      this.searchkit.state.book_full_title &&
      this.searchkit.state.book_full_title.length > 0;

    return (
      <SearchFilters
        isVisible={isVisible}
        useFullBookTitle={useFullBookTitle}
        className={className}
        style={style}
        onToggle={onToggle}
      />
    );
  }
}

export default SearchFiltersWrapper;
